import type { LegalContext } from '../../types/legal-context.types';
import { getApplicationBrandTheme } from '../../utils/brand-theme.utils';

interface ContactConsentFieldProps {
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  legalContext?: LegalContext | null;
}

const fallbackContactConsentText = (companyName: string, companyEmail: string) =>
  `By checking this box and selecting Continue the Borrower agrees to authorize ${companyName} to contact the Borrower at the telephone, cell phone, email or direct mail contact data provided in this form for purposes of fulfilling this inquiry about business financing, even if the Borrower has previously indicated a preference of "do not call" or "do not email" with a government registry or with ${companyName}. Also, the Borrower agrees that we may deliver a response to the pre-approval request to the email address provided. By agreeing to communicate with ${companyName} about this pre-approval request by email, the Borrower agrees to retain (whether by printing or saving electronically) the communications and documents that we email. If the Borrower is unable to print or retain the information, or wishes to revoke the agreement to be contacted about this qualification inquiry by email, the Borrower agrees to email ${companyName} at ${companyEmail}.`;

// Purpose: Renders a required first-page contact consent checkbox from legalContext copy.
export const ContactConsentField = ({
  value,
  onChange,
  disabled = false,
  isInvalid = false,
  legalContext = null,
}: ContactConsentFieldProps) => {
  const brandTheme = getApplicationBrandTheme();
  const companyName = legalContext?.company?.name || 'your broker';
  const companyEmail = legalContext?.company?.email || 'support@example.com';

  const consentText =
    legalContext?.clickwrap?.contactConsentText?.trim() ||
    fallbackContactConsentText(companyName, companyEmail);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return (
    <div className="mb-4">
      <label
        className={`flex items-start space-x-2 text-sm font-medium ${
          disabled ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700'
        }`}
      >
        <input
          type="checkbox"
          className={`mt-1 h-4 w-4 rounded ${
            disabled ? 'border-gray-200 bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          } ${isInvalid ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          style={{
            accentColor: disabled ? undefined : brandTheme.actionBackground,
          }}
          checked={value}
          onChange={handleChange}
          required={true}
          disabled={disabled}
          data-testid="contact-consent-checkbox"
        />
        <span className="flex-1 text-gray-700">{consentText}</span>
      </label>
    </div>
  );
};
